import { Box, Card, Grid, Typography } from '@mui/material';
import React from 'react';

import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { faUserTie } from '@fortawesome/sharp-regular-svg-icons';
import { useTheme } from '@mui/system';

export type GestionEtablissement = {
    custom_l2d: {
        siret: string;
        estPrincipal?: boolean;
    };
    personneMorale?: {
        autresEtablissements?: {
            descriptionEtablissement?: {
                dateEffetFermeture?: string;
                enseigne?: string;
                nomCommercial?: string;
            };
            adresse?: {
                voie?: string;
                codePostal?: string;
                commune?: string;
            };
        };
    };
};

export type GestionEtablissementWidgetCallbackArgs = (
    gestionEtablissement: GestionEtablissement,
    index: number,
) => void;

export type GestionEtablissementWidgetType = {
    gestionEtablissement: GestionEtablissement;
    actions?: Array<{
        name: string;
        icon?: React.ReactNode | React.FunctionComponent;
        tooltip?: string;
        text?: string;
        onClick: GestionEtablissementWidgetCallbackArgs | (() => void);
    }>;
    index?: number;
};

export const GestionEtablissementWidget: React.FunctionComponent<GestionEtablissementWidgetType> =
    function ({ gestionEtablissement, actions, index = -1 }: GestionEtablissementWidgetType) {
        const theme = useTheme();

        const enseigne =
            gestionEtablissement.personneMorale?.autresEtablissements?.descriptionEtablissement
                ?.enseigne;
        const nomCommercial =
            gestionEtablissement.personneMorale?.autresEtablissements?.descriptionEtablissement
                ?.nomCommercial;
        const estPrincipal = gestionEtablissement.custom_l2d?.estPrincipal;

        return (
            <Card
                sx={{
                    padding: '10px',
                    cursor: 'pointer',
                    margin: '15px 0',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: 'rgba(66,221,245,0.08)',
                        boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
                    },
                }}
            >
                <Grid
                    container
                    sx={{ marginLeft: '0px' }}
                >
                    <Grid
                        item
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            paddingLeft: '0px',
                        }}
                    >
                        <Box
                            display="flex"
                            flexDirection="row"
                            gap="10px"
                            sx={{ margin: '0 12px 0' }}
                        >
                            <Box
                                data-testid="test-id-status-color-box"
                                sx={{
                                    margin: '0px',
                                    padding: '10px',
                                    borderRadius: '10px',
                                    fontSize: '25px',
                                    maxHeight: '65px',
                                    backgroundColor: 'rgba(66,221,245,0.2)',
                                }}
                            >
                                <FaOptionIcon
                                    data-testid="test-id-mimetype-icone"
                                    icon={faUserTie}
                                    color={theme.palette.primary.main}
                                    sx={{ fontSize: '33px' }}
                                />
                            </Box>
                            <Box sx={{ padding: '0 10px', minWidth: '150px' }}>
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Siret : {gestionEtablissement.custom_l2d?.siret ?? ''}{' '}
                                    <span
                                        style={{
                                            fontSize: '0.75rem',
                                            color: estPrincipal
                                                ? theme.palette.primary.main
                                                : theme.palette.text.secondary,
                                            fontWeight: 'normal',
                                        }}
                                    >
                                        ({estPrincipal ? 'Principal' : 'Secondaire'})
                                    </span>
                                </Typography>
                                <Typography variant="h6">
                                    Adresse :{' '}
                                    {`${gestionEtablissement.personneMorale?.autresEtablissements?.adresse?.voie ?? ''} - ${gestionEtablissement.personneMorale?.autresEtablissements?.adresse?.codePostal ?? ''} ${gestionEtablissement.personneMorale?.autresEtablissements?.adresse?.commune ?? ''}`.toUpperCase()}
                                </Typography>
                                {enseigne && (
                                    <Typography variant="h6">Enseigne : {enseigne}</Typography>
                                )}
                                {nomCommercial && (
                                    <Typography variant="h6">
                                        Nom commercial : {nomCommercial}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <Box
                            display="flex"
                            flexDirection="row"
                            gap="10px"
                            sx={{ margin: '0 0 0 auto' }}
                        >
                            {actions
                                ? actions.map((action, key) => (
                                      <Box
                                          title={action.tooltip}
                                          data-testid={`test-id-${key}-handleClick-button`}
                                          onClick={() =>
                                              action.onClick(gestionEtablissement, index)
                                          }
                                          role="button"
                                          key={action.name}
                                          sx={{
                                              cursor: 'pointer',
                                              color: theme.palette.primary.main,
                                              fontWeight: 'bold',
                                              padding: '6px 12px',
                                              borderRadius: '8px',
                                              border: `1px solid ${theme.palette.primary.main}`,
                                              fontSize: '0.85rem',
                                              whiteSpace: 'nowrap',
                                              '&:hover': {
                                                  backgroundColor: 'rgba(66,221,245,0.1)',
                                              },
                                          }}
                                      >
                                          {action.icon || action.text}
                                      </Box>
                                  ))
                                : null}
                        </Box>
                    </Grid>
                </Grid>
            </Card>
        );
    };
